package br.com.heyagenda.data.repository

import br.com.heyagenda.data.remote.FirebaseAuthDataSource
import br.com.heyagenda.data.remote.FirestoreServiceDataSource
import br.com.heyagenda.data.remote.StudioServiceDto
import br.com.heyagenda.domain.model.StudioService
import br.com.heyagenda.domain.repository.ServiceRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

class ServiceRepositoryImpl(
    private val firestoreDataSource: FirestoreServiceDataSource,
    private val authDataSource: FirebaseAuthDataSource
) : ServiceRepository {

    private val localServices = MutableStateFlow<List<StudioService>>(emptyList())

    override fun getServices(): Flow<List<StudioService>> {
        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid == null) {
            println(">>> [ServiceRepo] User UID is null. Returning empty local services.")
            return localServices.asStateFlow()
        }

        return firestoreDataSource.observeServices(uid)
            .map { dtos ->
                dtos.map { it.toDomain() }
            }
    }

    override suspend fun saveService(service: StudioService): Result<Unit> {
        val uid = authDataSource.currentFirebaseUser?.uid
        val dto = service.toDto()

        if (uid != null) {
            val result = firestoreDataSource.saveService(uid, dto)
            if (result.isSuccess) {
                return result
            }
        }

        localServices.update { list ->
            val index = list.indexOfFirst { it.id == service.id }
            if (index >= 0) {
                list.toMutableList().apply { set(index, service) }
            } else {
                list + service.copy(id = service.id.ifBlank { "local_s_${list.size + 1}" })
            }
        }
        return Result.success(Unit)
    }

    override suspend fun deleteService(serviceId: String): Result<Unit> {
        localServices.update { list -> list.filterNot { it.id == serviceId } }

        val uid = authDataSource.currentFirebaseUser?.uid
        if (uid != null) {
            val result = firestoreDataSource.deleteService(uid, serviceId)
            if (result.isSuccess) {
                println(">>> [ServiceRepo] Successfully deleted service $serviceId from Firestore for $uid")
                return result
            }
        }

        return Result.success(Unit)
    }

    private fun StudioServiceDto.toDomain(): StudioService {
        return StudioService(
            id = id,
            name = name,
            category = category,
            priceFormatted = priceFormatted,
            durationMinutes = durationMinutes,
            description = description
        )
    }

    private fun StudioService.toDto(): StudioServiceDto {
        return StudioServiceDto(
            id = id,
            name = name,
            category = category,
            priceFormatted = priceFormatted,
            durationMinutes = durationMinutes,
            description = description
        )
    }
}
